import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';
import { z } from 'zod';

import {
  type INTEGRATION_PROVIDER_ID,
  mutateIntegrationConnectionAPIKeyCreate,
} from '@/api/integration';
import { Button } from '@/components/buttons';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/Dialog';
import { useAppForm } from '@/components/Form';
import { FieldError, FieldLabel } from '@/components/Form/fields';
import { Text } from '@/components/typography';
import SolAPIError from '@/error/SolAPIError';

import type { AuthMethodAPIToken } from '../types';

type Props = {
  providerId: INTEGRATION_PROVIDER_ID;
  authMethod: AuthMethodAPIToken | null;
  onClose: () => void;
  onOk: () => void;
};

const APITokenConnectionDialog = ({ providerId, authMethod, onClose, onOk }: Props) => {
  const defaultValues = useMemo(
    () =>
      Object.fromEntries((authMethod?.fields ?? []).map((field) => [field.id, ''])) as Record<
        string,
        string
      >,
    [authMethod],
  );
  const formSchema = useMemo(
    () =>
      z.object(
        Object.fromEntries(
          (authMethod?.fields ?? []).map((field) => [
            field.id,
            z.string().min(1, t`This field is required`),
          ]),
        ),
      ),
    [authMethod],
  );

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: formSchema,
      onSubmitAsync: async ({ value }) => {
        try {
          await mutateIntegrationConnectionAPIKeyCreate(providerId, value);
          onOk();
        } catch (e) {
          if (e instanceof SolAPIError) {
            const message = e.getFirstError().message;
            return {
              form: { message },
              fields: {},
            };
          }
          if (e instanceof Error) {
            return {
              form: { message: e.message },
              fields: {},
            };
          }
        }
      },
    },
  });

  return (
    <Dialog
      open={authMethod != null}
      onOpenChange={(isOpen) => {
        if (form.state.isSubmitting) return;
        if (!isOpen) onClose();
      }}
      onOpenChangeComplete={(isOpen) => {
        if (!isOpen) {
          form.reset(defaultValues);
        }
      }}
    >
      <DialogContent showCloseButton={!form.state.isSubmitting}>
        <DialogHeader>
          <DialogTitle>{authMethod?.title}</DialogTitle>
          <DialogDescription>
            <Trans>Provide the credentials below to connect your account.</Trans>
          </DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-6"
          onSubmit={(event) => {
            event.preventDefault();
            form.handleSubmit();
          }}
        >
          <div className="flex flex-col gap-2">
            {authMethod?.instructions.map((instruction, index) => (
              <Text key={index} variant="body1">
                {instruction}
              </Text>
            ))}
          </div>
          {authMethod?.fields.map((field) => (
            <form.AppField key={field.id} name={field.id}>
              {(fieldApi) => (
                <fieldApi.Field>
                  <FieldLabel htmlFor={field.id}>{field.label}</FieldLabel>
                  <fieldApi.TextInput id={field.id} placeholder={field.label} size="large" />
                  <fieldApi.Error />
                </fieldApi.Field>
              )}
            </form.AppField>
          ))}
          <form.Subscribe selector={(state) => [state.errors]}>
            {([errors]) => <FieldError errors={errors} />}
          </form.Subscribe>
        </form>
        <DialogFooter>
          <form.Subscribe selector={(state) => [state.isSubmitting]}>
            {([isSubmitting]) => (
              <DialogClose
                render={
                  <Button size="large" disabled={isSubmitting}>
                    <Trans>Cancel</Trans>
                  </Button>
                }
              />
            )}
          </form.Subscribe>
          <form.AppForm>
            <form.SubmitButton variant="on-primary" size="large">
              <Trans>Connect</Trans>
            </form.SubmitButton>
          </form.AppForm>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default APITokenConnectionDialog;
